import type { CartItem, CartBundleItem, ICartState } from '@/ui/store/cart/types';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import {
  BundleItemDetailsFragment,
  BundleImageFragment,
  BundleItemDetailsWithUpsellingFragment,
  ImageFragment,
  RichTextFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { CartUpsellingItemSection } from '@/ui/components/layout/cart/CartAddOnServices/CartUpsellingItemSection/types/CartUpsellingItemSection';
import { DocShapeId } from '@/utils/shapeIds';
import { BundleAdditionalInformation } from '@/ui/components/pages/ProductDetail/types/BundleAdditionalInformation';
import {
  countBundleSlots,
  parseAdditionalInformation,
  parseBundleProducts,
  parseSubscriptions,
} from '@/integrations/crystallize/helpers/bundle-products-helper';
import UpgradeProgramType from '@/constants/upgrade-program-type';
import { selectBundleImages } from '@/utils/bundle-helper';
import {
  createCartBundleItem,
  mapSelectedVariantsToCartItems,
} from '@/ui/services/utils/bundle-cart-items';
import { buildChooserParams, buildLinkPathForSelection } from '@/ui/services/utils/bundle-links';
import type { ProductVariantReference } from './BaseProductService';
import { BundleProduct } from './BundleProductService';
import { SelectedProductVariants } from '../components/pages/ProductDetail/types/SelectedProductVariants';
import { parseCartUpsellingContent } from '../components/pages/ProductDetail/SmartlockDetail/utils/parseCartUpsellingContent';

export enum ChooserDesignVariants {
  group = 'group',
  firstProduct = 'first-product',
}

/** A bundle together with the variants chosen for its products. */
export interface BundleConfiguration {
  bundleId: string;
  selectedVariants: ProductVariantReference[];
}

export class Bundle {
  id: string;

  documentExtRef: string | null;

  name: string;

  products: BundleProduct[];

  // The number of products the CMS lists; a product without a price here is dropped from
  // `products`, which then sells an incomplete set.
  slotCount: number;

  priceVariantId: string | undefined;

  images: BundleImageFragment[];

  pagePath?: string;

  cartUpselling?: CartUpsellingItemSection[];

  chooserDeisgnVariant: ChooserDesignVariants;

  description?: RichTextFragment;

  additionalInformation?: BundleAdditionalInformation;

  constructor(
    data: BundleItemDetailsFragment | BundleItemDetailsWithUpsellingFragment,
    priceVariantId?: string,
  ) {
    this.id = data.id;
    this.documentExtRef = data.externalReference || null;
    this.name = data.name!;
    this.images = componentByTypename(data.images?.content, 'ImageContent')?.images ?? [];
    if ('page' in data) {
      this.pagePath =
        componentByTypename(data.page?.content, 'ItemRelationsContent')?.items?.[0]?.path ??
        undefined;
    }
    this.products = [
      ...parseBundleProducts(data.bundleProducts),
      ...parseSubscriptions(data.bundleProducts, priceVariantId),
    ];
    this.slotCount = countBundleSlots(data.bundleProducts);
    this.priceVariantId = priceVariantId;
    this.chooserDeisgnVariant = componentByTypename(
      data.chooserDesignVariant?.content,
      'SelectionContent',
    )?.options?.[0]?.value as ChooserDesignVariants | ChooserDesignVariants.group;
    if ('cartUpselling' in data) {
      this.cartUpselling = parseCartUpsellingContent(data.cartUpselling);
    }
    this.description =
      componentByTypename(data.description?.content, 'RichTextContent') || undefined;

    if ('additionalInformation' in data) {
      this.additionalInformation = parseAdditionalInformation(data.additionalInformation);
    }
  }

  // Calculate total original price of all products in the bundle
  get totalOriginalPrice(): number {
    return this.getTotalOriginalPrice();
  }

  // Total original price for the selected variants, the default variant for every slot without one
  getTotalOriginalPrice(selected?: SelectedProductVariants): number {
    return this.products.reduce(
      (sum, product) => sum + product.getVariantPrice(product.resolveVariant(selected)),
      0,
    );
  }

  // Calculate total discount for the bundle
  get totalDiscount(): number {
    return this.products.reduce((sum, product) => sum + product.discount, 0);
  }

  // Calculate final price after applying discounts
  get finalPrice(): number {
    return this.getFinalPrice();
  }

  // Each slot is priced on its own, so a variant cheaper than its slot discount counts as free
  // instead of lowering the price of the rest of the set.
  getFinalPrice(selected?: SelectedProductVariants): number {
    return this.products.reduce((sum, product) => {
      const variant = product.resolveVariant(selected);
      // A subscription may carry a zero price variant, so its initial period is what is discounted.
      const slotPrice = variant.subscriptionPlan
        ? Math.max(0, product.getVariantPrice(variant) - product.discount)
        : product.getPriceAfterDiscount(variant);
      return sum + slotPrice;
    }, 0);
  }

  // What the set saves against its products bought one by one, for the given selection
  getSaving(selected?: SelectedProductVariants): number {
    return this.getTotalOriginalPrice(selected) - this.getFinalPrice(selected);
  }

  hasSelectableSlots(): boolean {
    return this.products.some((product) => product.isSelectable());
  }

  // SKU per slot: the selected variant, the default variant for every slot without one
  getSelectedSkus(selected?: SelectedProductVariants): string[] {
    return this.products
      .map((product) => product.resolveVariant(selected)?.sku)
      .filter((sku): sku is string => !!sku);
  }

  getImagesForSelection(selected?: SelectedProductVariants): BundleImageFragment[] {
    return selectBundleImages(this.images, this.getSelectedSkus(selected));
  }

  // Returns first image
  get firstImage(): ImageFragment | null {
    return this.images[0] ?? null;
  }

  get firstProductName(): string {
    return this.products[0]?.name || '';
  }

  get productNames(): string {
    return this.getProductNames();
  }

  getProductNames(separator = ', '): string {
    return this.products.map((product) => product.name).join(separator);
  }

  get hasFreeTrial(): boolean {
    return this.products.some((product) => product.hasFreeTrial);
  }

  get recurringSubscriptionPrice(): number {
    return this.products.reduce((sum, product) => sum + (product.recurringPeriod?.price ?? 0), 0);
  }

  get hasSubscription(): boolean {
    return this.products.some((product) => product.isSubscription);
  }

  get subscription(): BundleProduct | undefined {
    return this.products.find((product) => product.isSubscription);
  }

  // An upgrade bundle names its upgrade program in the external reference. Such
  // a bundle only sells after the device check, so callers treat it apart from
  // a bundle the visitor can buy as configured.
  get isUpgradeBundle(): boolean {
    return (
      !!this.documentExtRef &&
      (Object.values(UpgradeProgramType) as string[]).includes(this.documentExtRef)
    );
  }

  /** The bundle with the variants chosen for its products (the default variants when none). */
  getConfiguration(
    selectedVariants: SelectedProductVariants = this.defaultBundleProductVariants,
  ): BundleConfiguration {
    return {
      bundleId: this.id,
      selectedVariants: this.products.flatMap((bundleProduct) => {
        const variant = selectedVariants[bundleProduct.bundleProductInstanceId];
        return variant ? [{ productId: bundleProduct.id, sku: variant.sku }] : [];
      }),
    };
  }

  get defaultBundleProductVariants(): SelectedProductVariants {
    const selectedVariants: SelectedProductVariants = {};
    this.products.forEach((bundleProduct) => {
      if (bundleProduct.defaultVariant) {
        selectedVariants[bundleProduct.bundleProductInstanceId] = bundleProduct.defaultVariant;
      }
    });
    return selectedVariants;
  }

  // Picks one variant per slot from the given SKU list, the slot's default when none matches.
  // A set may list the same product twice, so each SKU fills one slot only, in slot order.
  selectVariantsBySKUs(skus: string[]): SelectedProductVariants {
    const used = skus.map(() => false);
    const result: SelectedProductVariants = {};
    this.products.forEach((bundleProduct) => {
      const index = skus.findIndex(
        (sku, i) => !used[i] && !!bundleProduct.getPurchasableVariantBySKU(sku),
      );
      const matched =
        index === -1 ? undefined : bundleProduct.getPurchasableVariantBySKU(skus[index]);
      if (matched) {
        used[index] = true;
      }
      result[bundleProduct.bundleProductInstanceId] = matched ?? bundleProduct.defaultVariant;
    });
    return result;
  }

  // Checks if bundle variant is in stock and adds it to the cart
  // If not in stock, shows notify me modal
  public checkStockAndAddToCart(
    selectedBundleProductVariants: SelectedProductVariants,
    cartStore: ICartState,
    quantity = 1,
  ) {
    if (!this.isBundleVariantInStock(selectedBundleProductVariants)) {
      cartStore.setShowNotifyMe(true, null, this);
      return;
    }
    const bundleCartItem = this.getBundleCartItems(selectedBundleProductVariants);
    if (!bundleCartItem) {
      console.error('Unable to create cart items for bundle', this, selectedBundleProductVariants);
      return;
    }
    cartStore.addBundle(bundleCartItem, { quantity });
    // Open the cart, but don't toggle it closed when it is already open
    if (!cartStore.isCartDisplayed) {
      cartStore.toggleCartDisplay();
    }
  }

  // Method to get the smartlock product from the bundle. Asumption is bundles always have max one smartlock
  public getSmartlock(): BundleProduct | undefined {
    return this.products.find((product) => product.shape === DocShapeId.smartLock);
  }

  // The chooser parameters for this bundle, shared by every link that opens the chooser
  public getChooserParams(): URLSearchParams {
    return buildChooserParams(this);
  }

  // Method to get the open chooser path for the bundle smartlock with the preselected bundle
  public getChooserPath(): string | undefined {
    const smartlock = this.getSmartlock();
    if (smartlock) {
      return `${smartlock.path}?${this.getChooserParams().toString()}`;
    }
    return undefined;
  }

  // Upgrade and subscription bundles only sell through the chooser, so they get no bundle page;
  // neither does a set missing a product, which the cart could not match to the bundle.
  isPageEligible(): boolean {
    return (
      this.products.length > 0 &&
      this.products.length === this.slotCount &&
      !this.isUpgradeBundle &&
      !this.hasSubscription
    );
  }

  getLinkablePagePath(): string | undefined {
    return this.isPageEligible() ? this.pagePath : undefined;
  }

  // The bundle page when one is linked and eligible, the chooser otherwise
  public getLinkPath(): string | undefined {
    return this.getLinkablePagePath() ?? this.getChooserPath();
  }

  // Same target as getLinkPath, opening with the given variants preselected
  public getLinkPathForSelection(selected?: SelectedProductVariants): string | undefined {
    return buildLinkPathForSelection(this, selected);
  }

  // Checks if at least some possible bundle variant is in stock - each bundle product has at least some variant available
  isSomeBundleVariantInStock() {
    return this.products.every((bundleProduct) => bundleProduct.isSomeVariantInStock());
  }

  // True when the country withholds the cylinder of a selected variant, as the product page does
  isSelectionRestrictedInCountry(
    selected: SelectedProductVariants | undefined,
    country?: string | null,
  ): boolean {
    return this.products.some((product) =>
      product.isVariantRestrictedInCountry(product.resolveVariant(selected), country),
    );
  }

  isBundleVariantInStock(selectedVariants?: SelectedProductVariants): boolean {
    if (!selectedVariants) {
      return false; // Selected bundle product variants are required
    }
    return this.products.every((bundleProduct) =>
      bundleProduct.isVariantInStock(bundleProduct.resolveVariant(selectedVariants)?.sku),
    );
  }

  // Method to retrieve cart items for the bundle based on selected variants
  getBundleCartItems(selectedVariantsForProducts: SelectedProductVariants): CartBundleItem | null {
    const mappedItems = mapSelectedVariantsToCartItems(this.products, selectedVariantsForProducts);
    const bundleCartItems = mappedItems.filter((item): item is CartItem => !!item);

    // Ensure there are no null items, otherwise return null
    if (bundleCartItems.length !== mappedItems.length) {
      return null;
    }

    return createCartBundleItem(this, bundleCartItems, selectedVariantsForProducts);
  }
}
